import { useEffect } from 'react'
import ReactDOM from 'react-dom'

interface SessionExpiredModalProps {
  isOpen: boolean
  onConfirm: () => void
}

export function SessionExpiredModal({ isOpen, onConfirm }: SessionExpiredModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  if (!isOpen) return null

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 overflow-hidden">
      {/* Ultra dark heavy frosted glass backdrop */}
      <div className="absolute inset-0 bg-black/65 backdrop-blur-3xl transition-opacity duration-300 animate-fadeIn" />

      {/* Modal Dialog Box */}
      <div
        className="relative flex flex-col w-full max-w-md bg-surface border border-border rounded-card shadow-2xl p-6 sm:p-8 text-center transition-all duration-300 z-10 animate-scaleUp"
        role="dialog"
        aria-modal="true"
        aria-labelledby="session-expired-title"
      >
        {/* Animated Badge Icon */}
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-status-idle/15 text-status-idle ring-8 ring-status-idle/10">
          <svg
            className="h-8 w-8 animate-pulse"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>

        {/* Title */}
        <h3 id="session-expired-title" className="text-xl sm:text-2xl font-bold text-text-primary tracking-tight mb-2">
          Session Expired
        </h3>

        {/* Description */}
        <p className="text-sm text-text-secondary leading-relaxed mb-6">
          Your authentication token has expired or is no longer valid. For security purposes, please sign in again to
          access the portal.
        </p>

        {/* Action Button */}
        <button
          onClick={onConfirm}
          className="w-full flex items-center justify-center gap-2 bg-primary hover:bg-primary-hover text-primary-fg font-semibold py-3 px-6 rounded-btn shadow-md transition-all duration-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <span>Sign In Again</span>
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </button>
      </div>
    </div>
  )

  return <>{ReactDOM.createPortal(modalContent, document.body)}</>
}
